import React, { useEffect, useState } from 'react';
import { StyleSheet, View, Platform, Switch, Linking, TouchableOpacity } from 'react-native';
import { useTheme } from '@emotion/react';
import { ODSIcon } from '../../../atoms/ODSIcon';
import { ODSText } from '../../../atoms/ODSText';
import ODSButton from '../../../atoms/ODSButton';
import ODSTabs from '../../../atoms/ODSTabs';

const RentReadySettingsCard = ({
  onClose,
  data,
  onSave,
  documents
}) => {
  const { colors } = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const initialSelectAll =
    data.is_tenant_info_shared &&
    data.is_right_to_rent_shared &&
    data.is_affordability_shared &&
    data.is_pre_reference_shared &&
    data.is_rental_ledger_shared &&
    data.is_id_verification_shared &&
    data.is_guarantor_affordability_shared;

  const [form, setForm] = useState({
    is_tenant_info_shared: data.is_tenant_info_shared,
    is_right_to_rent_shared: data.is_right_to_rent_shared,
    is_affordability_shared: data.is_affordability_shared,
    is_pre_reference_shared: data.is_pre_reference_shared,
    is_rental_ledger_shared: data.is_rental_ledger_shared,
    is_id_verification_shared: data.is_id_verification_shared,
    is_guarantor_affordability_shared: data.is_guarantor_affordability_shared,
    default_documents_shared: data.default_documents_shared || [],
    selectAll: initialSelectAll,
  });
  useEffect(() => {
    const allSwitchesOn =
      data.is_tenant_info_shared &&
      data.is_right_to_rent_shared &&
      data.is_affordability_shared &&
      data.is_pre_reference_shared &&
      data.is_rental_ledger_shared &&
      data.is_id_verification_shared &&
      data.is_guarantor_affordability_shared;

    setForm({
      is_tenant_info_shared: data.is_tenant_info_shared,
      is_right_to_rent_shared: data.is_right_to_rent_shared,
      is_affordability_shared: data.is_affordability_shared,
      is_pre_reference_shared: data.is_pre_reference_shared,
      is_rental_ledger_shared: data.is_rental_ledger_shared,
      is_id_verification_shared: data.is_id_verification_shared,
      is_guarantor_affordability_shared: data.is_guarantor_affordability_shared,
      default_documents_shared: data.default_documents_shared || [],
      selectAll: allSwitchesOn,
    });
  }, [data]);


  const handleSwitchChange = (name, value) => {
    if (name === 'selectAll') {
      setForm({
        ...form,
        selectAll: value,
        is_tenant_info_shared: value,
        is_right_to_rent_shared: value,
        is_affordability_shared: value,
        is_pre_reference_shared: value,
        is_rental_ledger_shared: value,
        is_id_verification_shared: value,
        is_guarantor_affordability_shared: value
      });
    } else {
      const updatedForm = {
        ...form,
        [name]: value,
      };

      // Check if all individual switches are on to determine selectAll state
      const allSwitchesOn =
        updatedForm.is_tenant_info_shared &&
        updatedForm.is_right_to_rent_shared &&
        updatedForm.is_affordability_shared &&
        updatedForm.is_pre_reference_shared &&
        updatedForm.is_rental_ledger_shared &&
        updatedForm.is_id_verification_shared &&
        updatedForm.is_guarantor_affordability_shared;

      updatedForm.selectAll = allSwitchesOn;
      setForm(updatedForm);
    }
  };

  const handleDocumentToggle = (id, value) => {
    let currentShared = [...(form.default_documents_shared || [])];
    if (value) {
      if (!currentShared.some(existingId => String(existingId) === String(id))) {
        currentShared.push(id);
      }
    } else {
      currentShared = currentShared.filter((docId) => String(docId) !== String(id));
    }
    setForm({
      ...form,
      default_documents_shared: currentShared,
    });
  };

  const handleSave = () => {
    onSave(form);
  };

  return (
    <View style={{ flex: 1, ...styles.shadowBox }}>
      <View style={{ justifyContent: 'center', alignItems: 'center', flexDirection: 'row', marginBottom: 12 }}>
        <ODSIcon icon={'TB-House'} size={24} color={colors.button.main} />
        <ODSText type='h5' marginLeft={20} color={colors.text.primary}>Rent Ready Settings</ODSText>
      </View>
      <ODSText textAlign='center' type='sm' marginTop={12} color={colors.text.primary}>
        Select the documents to share with your agent.
      </ODSText>
      <View style={{ marginVertical: 24 }}>
        <ODSTabs
          items={['General', 'Documents']}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          selectedColor={colors.main.blue[700]}
          color={colors.main.blue[500]}
        />
      </View>

      {activeTab === 0 ? (
        <>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
            <Switch
              value={form.selectAll}
              onValueChange={(value) => handleSwitchChange('selectAll', value)}
            />
            <ODSText type='md' marginLeft={20} color={colors.text.primary}>Select All</ODSText>
          </View>
          <View style={{ width: '100%', marginVertical: 24, height: 1, backgroundColor: colors.other['border-neutral'] }} />


          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 0 }}>
            <Switch
              value={true}
            // disabled={true}
            />
            <ODSText type='md' marginLeft={20} color={colors.text.primary}>ID Verification</ODSText>
          </View>
          <ODSText type='sm' marginTop={8} color={colors.text.primary}>You can't unshare a verified ID</ODSText>

          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 24 }}>
            <Switch
              value={form.is_affordability_shared}
              onValueChange={(value) => handleSwitchChange('is_affordability_shared', value)}
            />
            <ODSText type='md' marginLeft={20} color={colors.text.primary}>Tenant Affordability</ODSText>
          </View>

          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 24 }}>
            <Switch
              value={form.is_guarantor_affordability_shared}
              onValueChange={(value) => handleSwitchChange('is_guarantor_affordability_shared', value)}
            />
            <ODSText type='md' marginLeft={20} color={colors.text.primary}>Guarantor Affordability</ODSText>
          </View>

          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 24 }}>
            <Switch
              value={form.is_tenant_info_shared}
              onValueChange={(value) => handleSwitchChange('is_tenant_info_shared', value)}
            />
            <ODSText type='md' marginLeft={20} color={colors.text.primary}>Tenant Info</ODSText>
          </View>

          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 24 }}>
            <Switch
              value={form.is_right_to_rent_shared}
              onValueChange={(value) => handleSwitchChange('is_right_to_rent_shared', value)}
            />
            <ODSText type='md' marginLeft={20} color={colors.text.primary}>Right to Rent</ODSText>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 24 }}>
            <Switch
              value={form.is_pre_reference_shared}
              onValueChange={(value) => handleSwitchChange('is_pre_reference_shared', value)}
            />
            <ODSText type='md' marginLeft={20} color={colors.text.primary}>Rent Ready Reference</ODSText>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 24, marginBottom: 40 }}>
            <Switch
              value={form.is_rental_ledger_shared}
              onValueChange={(value) => handleSwitchChange('is_rental_ledger_shared', value)}
            />
            <ODSText type='md' marginLeft={20} color={colors.text.primary}>Rent Record</ODSText>
          </View>


        </>
      ) : (
        <View style={{ flex: 1 }}>
          {documents && documents?.length > 0 ? (
            <>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
                <Switch
                  value={documents.every(doc => form.default_documents_shared?.some(sharedId => String(sharedId) === String(doc.document_id)))}
                  onValueChange={(value) => {
                    const allIds = documents.map(d => d.document_id);
                    let newShared = [...(form.default_documents_shared || [])];
                    if (value) {
                      allIds.forEach(id => {
                        if (!newShared.some(existingId => String(existingId) === String(id))) {
                          newShared.push(id)
                        }
                      });
                    } else {
                      newShared = newShared.filter(id => !allIds.some(docId => String(docId) === String(id)));
                    }
                    setForm({ ...form, default_documents_shared: newShared });
                  }}
                />
                <ODSText type='md' marginLeft={20} color={colors.text.primary}>Select All</ODSText>
              </View>
              <View style={{ width: '100%', marginVertical: 24, height: 1, backgroundColor: colors.other['border-neutral'] }} />

              {documents.map((doc, index) => (
                <View key={index} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, marginVertical: 6 }}>
                  <Switch
                    value={form.default_documents_shared ? form.default_documents_shared.some(id => String(id) === String(doc.document_id)) : false}
                    onValueChange={(value) => handleDocumentToggle(doc.document_id, value)}
                  />
                  <View style={{ marginLeft: 20, marginRight: 8 }}>
                    <ODSIcon icon={'TBDoc'} size={24} color={colors.button.main} />
                  </View>
                  <View style={{ flex: 1 }}>
                    {/* <ODSText type='md' marginTop={0} color={colors.text.primary}>{doc.filename || 'Document'}</ODSText> */}
                    <TouchableOpacity onPress={() => Linking.openURL(doc.file_path || doc.url)}>
                      <ODSText type='md' textDecorationLine='underline' color={colors.text.secondary}>{doc.filename || 'Document'}</ODSText>
                    </TouchableOpacity>
                  </View>
                </View>
              ))}
              <View style={{ height: 28 }} />
            </>
          ) : (
            <View style={{ padding: 24, alignItems: 'center' }}>
              <ODSText type='md' marginTop={0} color={colors.text.secondary}>No documents found.</ODSText>
            </View>
          )}
        </View>
      )}
      <ODSButton onPress={handleSave}>Save</ODSButton>
      <ODSButton onPress={() => onClose()} restStyle={{ marginTop: 12 }} type='secondary'>Close</ODSButton>
    </View>
  );
};

const styles = StyleSheet.create({
  shadowBox: {
    backgroundColor: '#fff',
    marginHorizontal: 20,
    padding: 32,
    borderRadius: 12,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 1, height: 3 },
        shadowOpacity: 0.15,
        shadowRadius: 8,
      },
      android: {
        elevation: 4,
      },
      web: {
        shadowColor: '#000',
        shadowOffset: { width: 1, height: 3 },
        shadowOpacity: 0.15,
        shadowRadius: 8,
      }
    }),
  },
});

export default RentReadySettingsCard;

